/**
 * @fileoverview The Beds tab (/beds; D56, it was /locations): every bed, the
 * ones that need the gardener first (owner, 2026-10-16: "warn on the beds
 * page"), as a card, tile or row, with the Add bed placeholder leading; and
 * one bed open (/beds/:id, BedOpen).
 *
 * The list: "Beds · 4 beds · 2 need you"; in the cards density each bed is
 * its card as it was before D56 (LocationCard: its name, photos, Public and
 * Share, Notes, its plants with their stages and weeks, Add a new plant
 * here, Lately, This bed's season), with what
 * the bed is in one line under its name and, when it has jobs, This week:
 * its own jobs (three, then "2 more jobs"), counting only its plants (owner,
 * 2026-10-16: "where did all that go? You're making this worse"). Which jobs
 * are a bed's, its part of each and the beds' order are the server's (the
 * Today brief's todo.beds, useTodayBrief, the same read Today makes; ticks
 * through useTodoMarks); the page only draws them. Without a brief (no
 * postcode) the beds are listed as they come, with no jobs. A tile's or a
 * row's tap opens the bed's card in a pop-up (B157, BedQuickLook, as a plant's
 * tile opens its card); Edit bed is one tap further, from the card.
 *
 * One bed open (/beds/:id, BedOpen): the same card at the page's width,
 * with all its jobs. An address naming no bed in the garden (gone, or
 * archived) goes back to /beds.
 *
 * Holds what a location card opens: the bed's own quick look (B157), the edit dialog, a plant's quick look,
 * the whole-bed note and archive panels, and the Plants pop-up (D78 P2,
 * decision 6: useAddPlant), opened with the bed preset from its "Add a new
 * plant here": "Take or upload a photo" (the photos carry the bed; a photo
 * of its seed packets is read as packets, so the old "Add plants from seed
 * packets" row folds into it) or "Type it in" (the plant form, the bed
 * chosen). Add bed opens the Beds pop-up (useAddBed): "Take or upload a
 * photo" (a new bed from a photo of the bed or its packets, reviewed in
 * "Check and save" over this page) or "Type it in" (the bed dialog, without
 * its old dashed packet row). A location just added, or made from a photo,
 * leads the list for the rest of the visit. "Share your plant?" is asked
 * here too once a first photo is filed (ShareMoments).
 *
 * A link to /beds#location-12 brings that bed into view once the page is
 * drawn (useArrivalScroll); a plant's card and Seasons now link to the bed
 * itself (/beds/12), and the old /locations addresses are sent on by
 * LocationsRedirect.
 *
 * A link to /beds?edit=12&more=1 (D55, /locations?edit= before D56; no job sends one now, owner, 2026-10-16) means that
 * bed's Edit bed opens once the beds are read, "More about this bed" open,
 * and the address loses the question so a reload does not open it again.
 *
 * W24 Job H: the one list of beds carries what a card draws (each bed's
 * growing plants and their count, its Lately and its Share count), so Beds
 * reads no season brief, no archive and no share count per bed when it opens.
 * A plant tapped in a bed's list or its Lately, growing or removed, opens over
 * the page from the garden list at once, else once it is read by its id
 * (usePlantById); one that cannot be read goes to its own page. The search box
 * asks the server (useBedSearch, after the usual typing pause) and draws the
 * beds it answers; a search that failed says the standard sentence in place
 * of the beds (reported to Sentry, ServerFailureNote), never every bed. Edit
 * bed reads its questions only when it opens.
 *
 * Where: the routes /beds and /beds/:bedId (the Beds tab), inside LocationsProvider.
 * API: GET /locations?status=1 (LocationsContext: each bed with its plants,
 * plant_count, lately and sharing); GET /locations?status=1&q= (the search,
 * useBedSearch); GET /today/brief
 * (useTodayBrief: todo.beds and the jobs); POST /todo/marks (useTodoMarks); GET
 * /plants?plantObjId= (usePlantById, only for a tapped plant not in the
 * garden list); POST /locations/:id/plants/status (clear a bed);
 * POST /locations/:id/notes and POST /locations/:id/notes/undo (a note for
 * every plant, and its Undo); the plant form's save (addPlant); the
 * packet scan's own calls.
 * Settings: the density in localStorage (LOCATION_VIEW_STORAGE_KEY, through
 * locationViews); UNDO_TOAST_MS (the note's Undo)
 * (config/tunables/garden); #location-<id> in the address scrolls to that
 * bed.
 * Words (en/es): the server's for the jobs and the count; locations.title, locations.addLocation, locations.noBeds, locations.addFirstBed (the empty page, EmptyGarden),
 * locations.grid.loading, locations.grid.error, locations.grid.retry,
 * locations.bed.* (removed, noteAdded, noteUndone, undo, failed),
 * plants.form.saveFailed.
 * Failures: beds that could not be read because of a failure on our side
 * (D31, lib/serverFailure) say the standard sentence, reported to Sentry,
 * with the support address as a link and no Retry; a note's Undo that failed that
 * way says it in its toast in place of locations.bed.failed's "Try again".
 * Watch out: a bed added on this visit leads the list until the page is
 * left; the create route answers its row keyed location_id, not id.
 *
 * @module LocationGrid
 */

import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { useLocationsContext } from '@/shared/context/LocationsContextDefinition';
import { useQueryClient } from '@tanstack/react-query';
import { usePlantsContext } from '@/shared/hooks/usePlantsContext';
import { usePlantById } from '@/shared/hooks/usePlantById';
import { GrowLoader } from '@/shared/components/ui/GrowLoader';
import { GuidanceQuiet, GuidanceUpdating } from '@/shared/components/GuidanceUpdating';
import { Button } from '@/shared/components/ui/button';
import { LocationCard } from './LocationCard';
import { BedQuickLook } from './BedQuickLook';
import { BedOpen } from './BedOpen';
import { LocationTile } from './LocationTile';
import { LocationRow } from './LocationRow';
import { LocationToolbar } from './LocationToolbar';
import { arrangeLocations, loadLocationView, saveLocationView, useBedSearch, type LocationViewSettings } from '../lib/locationViews';
import { EditLocationDialog } from './EditLocationDialog';
import { AddPlaceholder } from '@/shared/components/ui/AddPlaceholder';
import { EmptyGarden } from '@/shared/components/ui/EmptyGarden';
import { PlantQuickLook } from '@/features/plant-list/components/PlantQuickLook';
import {
  addLocationNote, setLocationPlantsStatus, undoLocationNote, type Location, type LocationPlant,
} from '@/shared/api/locations';
import { toast } from '@/shared/components/ui/use-toast';
import { ToastAction } from '@/shared/components/ui/toast';
import { BedNotePanel, ClearBedPanel } from './BedActions';
import { ShareMoments } from '@/features/share/ShareMoments';
import type { PlantWritePayload } from '@/shared/api/plantCatalogue';
import { useAddPlant } from '@/features/add-by-photo/useAddPlant';
import { useAddBed } from '@/features/add-by-photo/useAddBed';
import { UNDO_TOAST_MS } from '@/config/tunables/garden';
import { ARCHIVED_PLANTS_QUERY_KEY, PLANTS_QUERY_KEY } from '@/config/tunables/ui';
import { gardenWritten } from '@/shared/lib/briefFreshness';
import { useArrivalScroll } from '@/shared/hooks/useArrivalScroll';
import { isServerFailure, serverFailureText } from '@/shared/lib/serverFailure';
import { useTodayBrief } from '@/shared/hooks/useTodayBrief';
import { useTodoMarks } from '@/features/today/lib/useTodoMarks';
import type { BriefBed } from '@/shared/api/todayBrief';
import { ServerFailureNote } from '@/shared/components/ServerFailure';

/** A link's #part naming one bed: #location-12. */
const BED_HASH = /^#(location-\d+)$/;

/**
 * The Locations page: every growing location the user has, with its plants.
 */
export const LocationGrid: React.FC = () => {
  const { reactTranslate: t } = useTranslation();
  const { locations, loading, error, refreshLocations, addLocation, updateLocation, deleteLocation, archiveLocation } = useLocationsContext();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  // null means "creating"; a location means "editing that one".
  const [editing, setEditing] = useState<Location | null>(null);
  /** The dialog opens with "More about this bed" open (a ?more=1 link, D55). */
  const [editMore, setEditMore] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  /** The bed open (/beds/:bedId), or none for the list. */
  const { bedId } = useParams<{ bedId?: string }>();
  /*
   * The one to-do list (D56): each bed's jobs and the beds' order are the
   * server's (the brief's todo.beds), the same read Today makes, so a tick
   * here is a tick there. Without a postcode there is no brief: the beds are
   * listed as they come, with no jobs.
   */
  const { brief, queryKey: briefKey, ask } = useTodayBrief();
  const todoMarks = useTodoMarks(briefKey, ask);
  const todo = brief?.todo ?? null;
  /** Each bed's place in the list, by id. */
  const bedById = React.useMemo(() => new Map((todo?.beds.list ?? []).map((bed) => [bed.id, bed])), [todo]);
  /**
   * A bed's place in the list, or null.
   *
   * @param id - The bed.
   */
  const briefBed = (id: number): BriefBed | null => bedById.get(id) ?? null;
  const editAsked = searchParams.get('edit');
  // ?edit=12&more=1: open that bed's Edit bed once the beds are read, then forget the question.
  useEffect(() => {
    if (!editAsked || loading) return;
    const asked = (locations ?? []).find((location) => String(location.id) === editAsked) ?? null;
    if (asked) {
      setEditing(asked);
      setEditMore(searchParams.get('more') === '1');
      setIsDialogOpen(true);
    }
    const rest = new URLSearchParams(searchParams);
    rest.delete('edit');
    rest.delete('more');
    setSearchParams(rest, { replace: true });
  }, [editAsked, loading, locations, searchParams, setSearchParams]);

  // How the page is read, and what is typed. The density outlives the page;
  // the search does not.
  const [settings, setSettings] = useState<LocationViewSettings>(loadLocationView);
  useEffect(() => { saveLocationView(settings); }, [settings]);
  /**
   * Changes some of the page's view settings (search, density); the density is remembered.
   *
   * @param patch - The settings to change.
   */
  const onChange = (patch: Partial<LocationViewSettings>) => setSettings((prev) => ({ ...prev, ...patch }));

  /*
   * The plant opened over this page: a bed lists its plants in its own thin
   * shape, and the card needs the whole plant. One in the garden list opens at
   * once (and stays in step with it: a photo added from inside the overlay
   * shows); a removed one, in a bed's Lately, is read by its id when tapped
   * (usePlantById, W24 Job H; the whole archive was read on every visit).
   */
  const { plants, addPlant: addToGarden } = usePlantsContext();
  const plantById = usePlantById(plants);
  // The Beds search, the server's answer (null while nothing is typed).
  const searched = useBedSearch(settings.search);

  // A link to one bed: brought into view once its card is drawn, below the header.
  const arrivedAt = BED_HASH.exec(useLocation().hash)?.[1] ?? '';
  useArrivalScroll(arrivedAt, !loading && Boolean(arrivedAt) && (locations ?? []).some((location) => `location-${location.id}` === arrivedAt));

  /**
   * Opens a plant here: at once from the garden list, else once it is read by
   * its id; a plant that cannot be read goes to its own page, which can
   * explain itself (usePlantById).
   *
   * @param plantObjId - The plant.
   */
  const openPlant = (plantObjId: number): void => plantById.open(plantObjId);

  /** Opens the bed dialog empty, to add a bed by typing ("Type it in"). */
  const openNewBed = () => { setEditing(null); setEditMore(false); setIsDialogOpen(true); };
  /** Opens the Beds pop-up (D78 P2). */
  const openCreate = () => addBed.open();
  /**
   * Opens the bed dialog on one bed.
   *
   * @param location - The bed.
   */
  const openEdit = (location: Location) => { setEditing(location); setEditMore(false); setIsDialogOpen(true); };
  /** The bed whose card is open in the pop-up (a tile or a row tapped, B157), by id; null when none. */
  const [openedBedId, setOpenedBedId] = useState<number | null>(null);
  /**
   * Opens the bed's card in the pop-up.
   *
   * @param location - The bed.
   */
  const openBedCard = (location: Location) => setOpenedBedId(location.id);
  /** The open bed, read from the page's list so a photo or note added in the pop-up shows at once. */
  const openedBed = openedBedId === null ? null : (locations ?? []).find((location) => location.id === openedBedId) ?? null;

  /*
   * Whole-bed actions: a note for every plant, and clearing the bed at the
   * end of the season. Only the plants growing there now take part.
   */
  const queryClient = useQueryClient();
  const [clearing, setClearing] = useState<Location | null>(null);
  const [noting, setNoting] = useState<Location | null>(null);
  /**
   * Adds the plant the form describes; its id, or null; a failure is thrown for the form to show.
   *
   * @param payload - What the plant form filled in.
   */
  const savePlant = async (payload: PlantWritePayload): Promise<number | null> => {
    const result = await addToGarden(payload);
    if (!result.success) throw new Error(result.error ?? t('plants.form.saveFailed'));
    return result.plant_obj_id != null ? Number(result.plant_obj_id) : null;
  };
  /**
   * A bed's plants growing now: the ones a whole-bed note or clearing takes part (the list sends only those, W24 Job H).
   *
   * @param location - The bed, or nothing.
   */
  const growing = (location: Location | undefined): LocationPlant[] => location?.plants ?? [];
  /** Reads the beds and plants again after a whole-bed change, and the week's outlook after them. */
  const refreshAfterBed = async (): Promise<void> => {
    await Promise.all([
      refreshLocations(),
      queryClient.invalidateQueries({ queryKey: [PLANTS_QUERY_KEY] }),
      queryClient.invalidateQueries({ queryKey: [ARCHIVED_PLANTS_QUERY_KEY] }),
    ]);
    // Grow's advice afresh: a bed's plants archived or back change its jobs on Today and here (D56).
    gardenWritten(queryClient);
  };
  /**
   * Clears a bed for the season: those plants archived. A removed plant is
   * removed, so the toast is a plain, short line with no Undo (B26).
   *
   * @param location - The bed.
   * @param plantObjIds - The plants ticked.
   */
  const clearBed = async (location: Location, plantObjIds: number[]): Promise<void> => {
    const removed = await setLocationPlantsStatus(location.id, plantObjIds, 0);
    await refreshAfterBed();
    const count = removed.length;
    toast({
      title: t(count === 1 ? 'locations.bed.removed_one' : 'locations.bed.removed', { count, name: location.name }),
    });
  };
  /**
   * Writes one note on each of those plants in a bed, with an Undo that takes the notes back.
   *
   * @param location - The bed.
   * @param content - The note.
   * @param plantObjIds - The plants ticked.
   */
  const noteBed = async (location: Location, content: string, plantObjIds: number[]): Promise<void> => {
    const notes = await addLocationNote(location.id, content, plantObjIds);
    await refreshAfterBed();
    const count = notes.length;
    const noteIds = notes.map((n) => n.noteId).filter((id): id is number => id !== null);
    toast({
      title: t(count === 1 ? 'locations.bed.noteAdded_one' : 'locations.bed.noteAdded', { count, note: content, name: location.name }),
      duration: UNDO_TOAST_MS,
      action: noteIds.length > 0 ? (
        <ToastAction altText={t('locations.bed.undo')} onClick={() => {
          void undoLocationNote(location.id, noteIds).then(refreshAfterBed).then(() => toast({ title: t('locations.bed.noteUndone') }))
            .catch((err: unknown) => toast({ title: serverFailureText(err, 'locations.noteBedUndo') ?? t('locations.bed.failed'), variant: 'destructive' }));
        }}>
          {t('locations.bed.undo')}
        </ToastAction>
      ) : undefined,
    });
  };
  // Locations added on this visit lead the list, as plants do on theirs.
  const [justAdded, setJustAdded] = useState<number[]>([]);
  // The Beds pop-up: a bed typed, or made from a photo (it then leads the list too).
  const addBed = useAddBed({
    onType: openNewBed,
    onFiled: ({ bedIds }) => setJustAdded((current) => [...new Set([...bedIds, ...current])]),
  });
  // A bed's "Add a new plant here": the Plants pop-up with the bed preset; typing opens the plant form with it chosen.
  const addPlant = useAddPlant({ onSave: savePlant });
  /**
   * Saves the bed dialog: changes to the bed open, or a new bed, which then leads the list.
   *
   * @param data - The dialog's multipart form.
   */
  const handleSave = async (data: FormData) => {
    if (editing) return updateLocation(editing.id, data);
    const made = await addLocation(data);
    // The create route answers with the row as inserted, keyed location_id.
    const made_ = made?.location as (Location & { location_id?: number }) | undefined;
    const id = made_?.id ?? made_?.location_id;
    if (id) setJustAdded((current) => [id, ...current]);
    return made;
  };

  if (loading) {
    return (
      <GrowLoader variant="screen" steps={[t('loader.beds.gather'), t('loader.beds.jobs')]} />
    );
  }

  if (error) {
    // A failure on our side (D31): the standard sentence, reported, and no Retry.
    if (isServerFailure(error)) {
      return (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <ServerFailureNote error={error} where="locations.grid" className="text-sm text-destructive" />
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <p className="text-sm text-destructive">{t('locations.grid.error')}</p>
        <Button onClick={() => void refreshLocations()}>{t('locations.grid.retry')}</Button>
      </div>
    );
  }

  /*
   * The server's order (D56): the beds that need the gardener first, by how
   * soon and how serious, then the rest as they were; a bed the list does not
   * know yet (just added) after them, in the order the beds came.
   */
  const rank = new Map((todo?.beds.list ?? []).map((bed, index) => [bed.id, index]));
  const arranged = arrangeLocations(locations, searched.found)
    .map((location, index) => ({ location, index }))
    .sort((a, b) => (rank.get(a.location.id) ?? rank.size + a.index) - (rank.get(b.location.id) ?? rank.size + b.index))
    .map(({ location }) => location);
  const items = justAdded.length === 0 ? arranged : [
    ...justAdded.map((id) => arranged.find((location) => location.id === id)).filter((l): l is Location => Boolean(l)),
    ...arranged.filter((location) => !justAdded.includes(location.id)),
  ];
  const total = locations?.length ?? 0;

  // One bed open (/beds/:id): its week, plants, About this bed and the rest of its card; an unknown or archived one goes back to the list.
  const openBed = bedId ? (locations ?? []).find((location) => String(location.id) === bedId) ?? null : null;
  if (bedId && !openBed) return <Navigate to="/beds" replace />;

  return (
    /* No beds yet: the same max-w-xl column Today and the empty Plants page use,
          so the title, toolbar and first step are not stretched across a desktop. */
    <div className={cn("p-4", total === 0 && "mx-auto w-full max-w-xl")}>
      {/* "Updating your garden..." while a fresh brief is on its way; the old beds and jobs below look quieter until it arrives. */}
      <GuidanceUpdating where="Beds" className="mb-4" />
      <GuidanceQuiet>
      {openBed ? (
        <BedOpen
          location={openBed}
          bed={briefBed(openBed.id)}
          todo={todo}
          marks={todoMarks}
          lately={openBed.lately}
          onEdit={openEdit}
          onOpenPlant={openPlant}
          onAddPlant={() => addPlant.open(openBed.id)}
          onNoteAll={() => setNoting(openBed)}
          onClearAll={() => setClearing(openBed)}
        />
      ) : (
      <>
      <div className="mb-4 flex items-baseline justify-between gap-2">
        <h1 className="text-2xl font-semibold text-foreground font-body">
          {t('locations.title')}
        </h1>
        {/* "4 beds · 2 need you", the server's. */}
        {total > 0 && todo && <span className="text-sm text-muted-foreground">{todo.beds.count}</span>}
      </div>

      <LocationToolbar settings={settings} onChange={onChange} shown={searched.error ? 0 : items.length} total={total} />
      {/* A search that failed on our side: the standard sentence, reported, and no beds rather than every bed. */}
      {searched.error && <ServerFailureNote error={searched.error} where="locations.search" className="mb-4 text-sm text-destructive" />}

      {/* No beds at all: the same empty page as the Plants tab, and no plus
          while there is nothing to put it in front of. Once there is a bed the
          placeholder that adds one leads in every density. */}
      {total === 0 && (
        <EmptyGarden message={t('locations.noBeds')}>
          <Button onClick={openCreate}>{t('locations.addFirstBed')}</Button>
        </EmptyGarden>
      )}
      {total > 0 && !searched.error && settings.view === 'cards' && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {/* Its own height, not the row's: a card-sized placeholder next to a
              tall card read as an empty card. Wrapped as on the Plants tab. */}
          <div className="flex w-full flex-col items-center">
            <AddPlaceholder shape="card" label={t('locations.addLocation')} onClick={openCreate} />
          </div>
          {items.map((location) => (
            <div key={location.id} id={`location-${location.id}`} className="scroll-mt-20">
              <LocationCard
                location={location}
                onEdit={openEdit}
                onOpenPlant={openPlant}
                onAddPlant={() => addPlant.open(location.id)}
                onNoteAll={() => setNoting(location)}
                onClearAll={() => setClearing(location)}
                todo={todo}
                bed={briefBed(location.id)}
                marks={todoMarks}
                jobsShown={todo?.shown}
              />
            </div>
          ))}
        </div>
      )}
      {total > 0 && !searched.error && settings.view === 'grid' && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          <AddPlaceholder shape="tile" label={t('locations.addLocation')} onClick={openCreate} />
          {items.map((location) => (
            <div key={location.id} id={`location-${location.id}`} className="scroll-mt-20">
              <LocationTile location={location} onOpen={openBedCard} />
            </div>
          ))}
        </div>
      )}
      {total > 0 && !searched.error && settings.view === 'list' && (
        <div className="divide-y rounded-sm border bg-card">
          <AddPlaceholder shape="row" label={t('locations.addLocation')} onClick={openCreate} />
          {items.map((location) => (
            <div key={location.id} id={`location-${location.id}`} className="scroll-mt-20">
              <LocationRow location={location} onOpen={openBedCard} />
            </div>
          ))}
        </div>
      )}
      </>
      )}
      </GuidanceQuiet>
      <BedQuickLook
        location={openedBed}
        onClose={() => setOpenedBedId(null)}
        onEdit={openEdit}
        onOpenPlant={openPlant}
        onAddPlant={openedBed ? () => addPlant.open(openedBed.id) : undefined}
        onNoteAll={openedBed ? () => setNoting(openedBed) : undefined}
        onClearAll={openedBed ? () => setClearing(openedBed) : undefined}
        todo={todo}
        bed={openedBed ? briefBed(openedBed.id) : null}
        marks={todoMarks}
        jobsShown={todo?.shown}
      />
      <PlantQuickLook plant={plantById.plant} onClose={plantById.close} />

      <BedNotePanel
        open={noting !== null}
        onOpenChange={(open) => { if (!open) setNoting(null); }}
        bedName={noting?.name ?? ''}
        plants={growing(noting ?? undefined)}
        onConfirm={(content, ids) => (noting ? noteBed(noting, content, ids) : Promise.resolve())}
      />
      <ClearBedPanel
        open={clearing !== null}
        onOpenChange={(open) => { if (!open) setClearing(null); }}
        bedName={clearing?.name ?? ''}
        plants={growing(clearing ?? undefined)}
        onConfirm={(ids) => (clearing ? clearBed(clearing, ids) : Promise.resolve())}
        onArchiveQuietly={async (ids) => { if (clearing) await setLocationPlantsStatus(clearing.id, ids, 0); }}
        onDeleted={refreshAfterBed}
      />

      {addPlant.element}

      <EditLocationDialog
        location={editing}
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSave={handleSave}
        onDelete={deleteLocation}
        onArchive={archiveLocation}
        onArchiveAll={editing ? () => { setIsDialogOpen(false); setClearing(editing); } : undefined}
        moreOpen={editMore}
      />
      {addBed.element}
      {/* "Share your plant?" once a first photo is filed here (D78: a plant made from a photo on Beds). */}
      <ShareMoments plants={plants ?? []} />
    </div>
  );
};
